import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { Game, Team } from '../types';
import GameCard from '../components/GameCard';
import TeamThemeProvider from '../components/theme/TeamThemeProvider';
import { parseGameTime } from '../utils/gameTime';
import { mediaUrl } from '../utils/mediaUrl';

const fmtDate = (s: string) =>
  parseGameTime(s).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

// ── Section header (editorial style with color accent bar) ────────────────────
const SectionHeader = ({ title, linkTo, linkLabel }: { title: string; linkTo?: string; linkLabel?: string }) => (
  <div className="section-header">
    <div className="section-title">
      <div className="section-title-accent" />
      <h2 className="section-title-text">{title}</h2>
    </div>
    {linkTo && <Link to={linkTo} className="section-link">{linkLabel || 'See all →'}</Link>}
  </div>
);

// ── Hero game — featured live/upcoming game ────────────────────────────────────
const HeroGame = ({ game, teams }: { game: Game; teams: Team[] }) => {
  const homeTeam = teams.find(t => t.id === game.team_id);
  const isLive   = game.status === 'live';
  const hasScore = game.home_final_score != null && game.away_final_score != null;

  const homeAbbr = (game.home_team || '?').slice(0, 3).toUpperCase();
  const awayAbbr = (game.away_team || '?').slice(0, 3).toUpperCase();
  const primary   = homeTeam?.primary_color   || '#0F2748';
  const secondary = homeTeam?.secondary_color || '#FFC72C';

  return (
    <TeamThemeProvider primary={primary} secondary={secondary}>
      <div className="hero-game" style={{ marginBottom: 32 }}>
        {/* Status line */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 20 }}>
          {isLive ? (
            <span className="live-badge">Live Now</span>
          ) : (
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-on-dark-muted)' }}>
              {fmtDate(game.scheduled_at)}
            </span>
          )}
          <span style={{ fontSize: 13, color: 'var(--ink-on-dark-muted)', fontWeight: 500 }}>
            {game.sport === 'baseball' ? '⚾' : game.sport === 'softball' ? '🥎' : game.sport === 'soccer' ? '⚽' : '🏀'} {game.sport}
          </span>
        </div>

        {/* Score block */}
        <div className="hero-score-block">
          {/* Away team */}
          <div className="hero-team-col">
            <div className="hero-team-mark" style={{ background: 'rgba(255,255,255,0.15)', color: 'white' }}>
              {awayAbbr}
            </div>
            <div className="hero-team-name">{game.away_team}</div>
            {game.status === 'completed' && hasScore && (
              <div className="hero-team-record">Final</div>
            )}
          </div>

          {/* Score */}
          <div className="hero-vs">
            {hasScore ? (
              <div className="hero-score-nums">
                <span>{game.away_final_score}</span>
                <span className="hero-score-sep"> – </span>
                <span>{game.home_final_score}</span>
              </div>
            ) : (
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 800, color: 'var(--ink-on-dark-muted)' }}>
                vs
              </div>
            )}
            {game.location && (
              <div style={{ fontSize: 12, color: 'var(--ink-on-dark-muted)', fontWeight: 500, marginTop: 4 }}>
                📍 {game.location}
              </div>
            )}
          </div>

          {/* Home team */}
          <div className="hero-team-col">
            <div className="hero-team-mark" style={{ background: secondary, color: 'var(--team-secondary-on)', overflow: 'hidden' }}>
              {homeTeam?.logo_url
                ? <img src={mediaUrl(homeTeam.logo_url)} alt={homeAbbr} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : homeAbbr}
            </div>
            <div className="hero-team-name">{game.home_team}</div>
            {homeTeam && (
              <div className="hero-team-record">Home</div>
            )}
          </div>
        </div>

        {/* CTA */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 24 }}>
          {isLive && (
            <Link
              to={`/watch/${game.id}`}
              style={{ background: 'white', color: primary, padding: '10px 24px', borderRadius: 8, fontWeight: 800, fontSize: 14, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              ▶ Watch Live
            </Link>
          )}
          {game.status === 'completed' && (
            <Link
              to={`/watch/${game.id}`}
              style={{ background: 'rgba(255,255,255,0.15)', color: 'white', padding: '10px 24px', borderRadius: 8, fontWeight: 700, fontSize: 14, textDecoration: 'none', border: '1px solid rgba(255,255,255,0.25)' }}
            >
              Watch Replay
            </Link>
          )}
          {homeTeam && (
            <Link
              to={`/teams/${homeTeam.id}`}
              style={{ background: 'rgba(255,255,255,0.12)', color: 'white', padding: '10px 20px', borderRadius: 8, fontWeight: 600, fontSize: 14, textDecoration: 'none', border: '1px solid rgba(255,255,255,0.2)' }}
            >
              Team Page →
            </Link>
          )}
        </div>
      </div>
    </TeamThemeProvider>
  );
};

// ── Team row on the teams list ────────────────────────────────────────────────
const TeamRow = ({ team }: { team: Team }) => {
  const rec = (team as any).record as { wins: number; losses: number; ties: number } | undefined;
  return (
    <Link
      to={`/teams/${team.id}`}
      style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 'var(--r-lg)', transition: 'box-shadow 0.15s' }}
      onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 2px 12px rgba(11,18,32,0.08)')}
      onMouseLeave={e => (e.currentTarget.style.boxShadow = '')}
    >
      <div style={{ width: 44, height: 44, borderRadius: 10, background: team.primary_color || '#0F2748', display: 'grid', placeItems: 'center', flexShrink: 0, overflow: 'hidden' }}>
        {team.logo_url
          ? <img src={mediaUrl(team.logo_url)} alt={team.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <span style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 16, color: 'white' }}>{team.name.charAt(0)}</span>}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 800, color: 'var(--ink-1)', letterSpacing: '-0.01em' }}>{team.name}</div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 500 }}>
          {team.sport} {team.season_year ? `· ${team.season_year}` : ''}
        </div>
      </div>
      {rec && (
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 800, color: 'var(--ink-2)', fontVariantNumeric: 'tabular-nums' }}>
          {rec.wins}–{rec.losses}{rec.ties > 0 ? `–${rec.ties}` : ''}
        </div>
      )}
      <i className="ti ti-chevron-right" style={{ color: 'var(--ink-4)', fontSize: 18 }} />
    </Link>
  );
};

// ── Main Home page ─────────────────────────────────────────────────────────────
const Home = () => {
  const [games, setGames]   = useState<Game[]>([]);
  const [teams, setTeams]   = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [gr, tr] = await Promise.all([api.get('/games'), api.get('/teams')]);
      setGames(gr.data);
      setTeams(tr.data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 30000);
    return () => clearInterval(t);
  }, [fetchData]);

  const liveGames     = games.filter(g => g.status === 'live').sort((a, b) => parseGameTime(a.scheduled_at).getTime() - parseGameTime(b.scheduled_at).getTime());
  const upcomingGames = games.filter(g => g.status === 'scheduled').sort((a, b) => parseGameTime(a.scheduled_at).getTime() - parseGameTime(b.scheduled_at).getTime());
  const recentGames   = games.filter(g => g.status === 'completed').sort((a, b) => parseGameTime(b.scheduled_at).getTime() - parseGameTime(a.scheduled_at).getTime()).slice(0, 6);

  const featuredGame  = liveGames[0] || upcomingGames[0];

  if (loading) return (
    <div className="container" style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--ink-4)' }}>
      Loading…
    </div>
  );

  return (
    <div className="container" style={{ paddingTop: 24, paddingBottom: 60 }}>

      {/* Featured game hero */}
      {featuredGame && <HeroGame game={featuredGame} teams={teams} />}

      {/* Live now */}
      {liveGames.length > 0 && (
        <section style={{ marginBottom: 40 }}>
          <SectionHeader title="Live Now" />
          <div className="games-grid">
            {liveGames.map(g => <GameCard key={g.id} game={g} teams={teams} />)}
          </div>
        </section>
      )}

      {/* Two-column layout: upcoming + teams side by side on desktop */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 320px', gap: 32, alignItems: 'start' }}>

        {/* Left: Upcoming → Recent */}
        <div>
          {upcomingGames.length > 0 && (
            <section style={{ marginBottom: 40 }}>
              <SectionHeader title="Upcoming Games" linkTo="/games" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {upcomingGames.slice(0, 5).map(g => <GameCard key={g.id} game={g} teams={teams} />)}
              </div>
              {upcomingGames.length > 5 && (
                <Link to="/games" style={{ display: 'block', textAlign: 'center', marginTop: 14, color: 'var(--sr-teal)', fontWeight: 600, fontSize: 14 }}>
                  +{upcomingGames.length - 5} more games →
                </Link>
              )}
            </section>
          )}

          {recentGames.length > 0 && (
            <section style={{ marginBottom: 40 }}>
              <SectionHeader title="Recent Results" linkTo="/games" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {recentGames.map(g => <GameCard key={g.id} game={g} teams={teams} />)}
              </div>
            </section>
          )}

          {games.length === 0 && (
            <div style={{ textAlign: 'center', padding: '80px 20px', background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 'var(--r-xl)' }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>🏟️</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 800, color: 'var(--ink-1)', marginBottom: 8 }}>No games yet</div>
              <div style={{ fontSize: 14, color: 'var(--ink-3)' }}>Games will appear here once they're scheduled.</div>
            </div>
          )}
        </div>

        {/* Right: Teams sidebar */}
        <div style={{ position: 'sticky', top: 88 }}>
          <SectionHeader title="Teams" linkTo="/teams" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {teams.slice(0, 8).map(t => <TeamRow key={t.id} team={t} />)}
          </div>
          {teams.length > 8 && (
            <Link to="/teams" style={{ display: 'block', textAlign: 'center', marginTop: 14, color: 'var(--sr-teal)', fontWeight: 600, fontSize: 14 }}>
              All {teams.length} teams →
            </Link>
          )}
        </div>

      </div>

      {/* Mobile: single column collapses automatically */}
      <style>{`
        @media (max-width: 900px) {
          .home-two-col { grid-template-columns: 1fr !important; }
        }
      `}</style>

    </div>
  );
};

export default Home;
